import * as RadixSwitch from '@radix-ui/react-switch';
import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  'aria-label'?: string;
  id?: string;
  name?: string;
  className?: string;
}

/** Square-cornered switch (2px radius, never a pill): 28x14 track, 10px thumb, brand when on. */
export function Switch({ checked, defaultChecked, onCheckedChange, disabled, label, description, id, name, className, ...aria }: SwitchProps) {
  const generated = useId();
  const switchId = id ?? `sw-${generated}`;
  const control = (
    <RadixSwitch.Root
      id={switchId}
      name={name}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={aria['aria-label']}
      className="relative inline-flex h-3.5 w-7 shrink-0 items-center rounded-sm border border-line bg-bg transition-colors hover:border-mute/60 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-brand data-[state=checked]:bg-brand"
    >
      <RadixSwitch.Thumb className="block h-2.5 w-2.5 translate-x-px rounded-sm bg-mute transition-transform data-[state=checked]:translate-x-[15px] data-[state=checked]:bg-white motion-reduce:transition-none" />
    </RadixSwitch.Root>
  );
  if (!label) return <span className={className}>{control}</span>;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <label htmlFor={switchId} className={cn('min-w-0 text-nav leading-snug text-ink', disabled ? 'opacity-50' : 'cursor-pointer')}>
        {label}
        {description ? <span className="mt-0.5 block text-xs text-mute">{description}</span> : null}
      </label>
      <span className="pt-0.5">{control}</span>
    </div>
  );
}
